import { Dialog, DialogContent, IconButton, Stack, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { capitalize } from 'lodash';
import { Icon } from '@iconify/react';
import { Close } from '@mui/icons-material';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMailContext } from 'src/section/mail/MailContext';
import { useSnackbar } from 'notistack';
import useAuth from 'src/hooks/useAuth';
import Iconify from 'src/components/Iconify';
import useToggle from 'src/hooks/useToggle';

/**
 * Renders a dialog component that displays a custom mail form.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.open - Determines whether the dialog is open or not.
 * @param {function} props.onClose - A callback function to close the dialog.
 * @returns {JSX.Element} - The rendered dialog component.
 */
export default function MailV2CustomLogin({ open, onClose }) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent
        sx={{
          width: 400
        }}
      >
        <Stack width={1} pb={1} direction={'row'} justifyContent={'space-between'}>
          <Typography variant={'h6'}>Lier un compte mail</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
        <CustomMailForm onClose={onClose} />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Renders a form for custom mail account configuration.
 *
 * @param {Object} props - The component props.
 * @param {function} props.onClose - A callback function to close the dialog.
 * @returns {JSX.Element} - The rendered form component.
 */
export const CustomMailForm = ({ onClose }) => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { loginCustomMail } = useMailContext();

  const validationSchema = Yup.object().shape({
    email: Yup.string().email('email non valide').required('Ce champs est requis '),
    password: Yup.string().required('Ce champs est requis '),

    smtp_host: Yup.string().required('Ce champs est requis '),
    smtp_port: Yup.number().required('Ce champs est requis '),

    imap_host: Yup.string().required('Ce champs est requis '),
    imap_port: Yup.number().required('Ce champs est requis ')
  });

  const initialValues = {
    email: '',
    password: '',

    smtp_host: '',
    smtp_port: undefined,

    imap_host: '',
    imap_port: undefined
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values, formikHelpers) => {
      const data = {
        userId: user?.id,
        user: {
          email: values?.email,
          password: values?.password,
          displayName: user?.displayName
        },
        smtp: {
          host: values?.smtp_host,
          port: values?.smtp_port
        },
        imap: {
          host: values?.imap_host,
          port: values?.imap_port
        }
      };

      loginCustomMail(data, onClose, (message) => {
        formikHelpers.setSubmitting(false);
        enqueueSnackbar(message, { variant: 'error' });
      });

      //console.log(data);
    }
  });

  const { getFieldProps, touched, errors, handleSubmit, isSubmitting } = formik;

  const getField = (name) => {
    return {
      ...getFieldProps(name),
      error: Boolean(touched[name] && errors[name]),
      helperText: touched[name] && errors[name]
    };
  };

  return (
    <Stack spacing={2}>
      <FormField label={'Email'} icon={'arcticons:spike-email'} {...getField('email')} />

      <FormField label={'Mot de passe'} icon={'arcticons:password'} {...getField('password')} isPassword />

      <ServerForm label={'Serveur SMTP'} hostProps={getField('smtp_host')} portProps={getField('smtp_port')} />

      <ServerForm label={'Serveur IMAP'} hostProps={getField('imap_host')} portProps={getField('imap_port')} />

      <LoadingButton
        onClick={handleSubmit}
        loading={isSubmitting}
        variant={'contained'}
        sx={{
          backgroundColor: 'primary.main'
        }}
      >
        Se connecter
      </LoadingButton>
    </Stack>
  );
};

/**
 * Renders a form for configuring SMTP and IMAP server settings.
 *
 * @param {Object} props - The component props.
 * @param {string} props.label - The label for the server form.
 * @param {Object} props.hostProps - The props for the host text field.
 * @param {Object} props.portProps - The props for the port text field.
 * @returns {JSX.Element} The server form component.
 */
const ServerForm = ({ label, hostProps, portProps }) => {
  return (
    <Stack width={1} spacing={1}>
      <Typography pl={0.5} fontSize={14}>
        {capitalize(label)}
      </Typography>
      <Stack width={1} direction={'row'} spacing={1}>
        <Stack width={'70%'}>
          <TextField label={'Hôte'} placeholder={'hôte'} size={'small'} {...hostProps} />
        </Stack>
        <Stack spacing={1} width={'30%'}>
          <TextField label={'Port'} placeholder={'port'} size={'small'} type={'number'} {...portProps} />
        </Stack>
      </Stack>
    </Stack>
  );
};

/**
 * Renders a form field with a label, icon, and optional password visibility toggle.
 *
 * @param {Object} props - The component props.
 * @param {string} props.label - The label for the form field.
 * @param {string} [props.helperText] - Optional helper text to display below the form field.
 * @param {boolean} [props.error] - Whether the form field has an error.
 * @param {React.ReactNode} [props.anchor] - Optional anchor element to render next to the form field.
 * @param {Object} [props.InputProps] - Additional props to pass to the TextField component.
 * @param {string} props.icon - The icon to display next to the form field.
 * @param {boolean} [props.isPassword] - Whether the form field is a password field.
 * @param {React.ReactNode} [props.endDecodation] - Optional element to render at the end of the form field.
 * @param {any} [rest] - Additional props to pass to the TextField component.
 * @returns {JSX.Element} The form field component.
 */
const FormField = ({
  label,
  helperText,
  error,
  anchor,
  InputProps,
  icon,
  isPassword,
  endDecodation = null,
  ...rest
}) => {
  const iconSize = { height: 20, width: 20 };
  const { open, handleSwitch } = useToggle();

  return (
    <Stack spacing={1}>
      <Typography pl={0.5} fontSize={14}>
        {capitalize(label)}
      </Typography>
      <TextField
        label={' '}
        size={'small'}
        type={isPassword && !open ? 'password' : 'text'}
        {...rest}
        InputProps={{
          startAdornment: (
            <IconButton size="small" sx={{ mr: 2 }}>
              <Iconify icon={icon} {...iconSize} />
            </IconButton>
          ),
          ...(isPassword && {
            endAdornment: (
              <IconButton size="small" onClick={handleSwitch}>
                <Iconify icon={open ? 'ic:outline-visibility' : 'ic:outline-visibility-off'} {...iconSize} />
              </IconButton>
            )
          })
        }}
      />
      {helperText && (
        <Typography variant={'caption'} color={error ? 'red' : 'black'} fontSize={12}>
          {helperText}
        </Typography>
      )}
    </Stack>
  );
};
